import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

export function usePasswordCheck() {
  const { user, supabase } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [isChecking, setIsChecking] = useState(false)

  useEffect(() => {
    const checkPasswordRequirement = async () => {
      // Skip check for public pages, invitation paths, and if already on set-password
      const publicPaths = ['/login', '/signup', '/reset-password', '/set-password', '/', '/waitlist', '/beta', '/auth/callback', '/plans']
      if (!user ||
          publicPaths.includes(location.pathname) ||
          location.pathname.startsWith('/auth/') ||
          location.pathname.startsWith('/invitation/')) {
        return
      }

      setIsChecking(true)

      try {
        // First, check if user has a password identity - if they do, they don't need to set password
        const session = await supabase.auth.getSession()
        if (session.data.session?.user) {
          const userIdentities = session.data.session.user.identities || []
          const hasPasswordIdentity = userIdentities.some(identity =>
            identity.provider === 'email'
          )

          // If user signed up with email/password, they already have a password - skip all checks
          if (hasPasswordIdentity) {
            console.log('User has password identity, skipping password check')
            setIsChecking(false)
            return
          }

          console.log('Checking auth methods for password requirement:', {
            userId: user.id,
            identities: userIdentities.map(i => i.provider)
          })
        }

        // Check if user needs to set password
        const { data, error } = await supabase
          .from('user_account_roles')
          .select('password_set')
          .eq('user_id', user.id)
          .maybeSingle() // Use maybeSingle to handle no rows gracefully

        // If we can check the flag and it's false, redirect
        if (!error && data && data.password_set === false) {
          navigate('/set-password', { replace: true })
          return
        }

        // If column doesn't exist (error) or no data, check session for magic link users
        if (session.data.session?.user) {
          const userMetadata = session.data.session.user.user_metadata || {}
          const hasPasswordInMetadata = userMetadata.password_set === true

          // If no password in metadata and no password identity, redirect
          if (!hasPasswordInMetadata) {
            console.log('Redirecting to password setup - user needs to set password')
            navigate('/set-password', { replace: true })
          }
        }
      } catch (err) {
        console.error('Error checking password requirement:', err)
      } finally {
        setIsChecking(false)
      }
    }

    checkPasswordRequirement()
  }, [user, location.pathname, navigate, supabase])

  return { isChecking }
}